import { useContract } from '@thirdweb-dev/react';
import { useAsync } from 'react-use';

import { bigNumberToDecimal } from '@app/helpers/format';
import { Pair } from '@app/types/pool';

import erc20 from '../../../abis/erc20.json';

export function useWithdrawLiquidityOutput(
  pairAddress: string,
  amount: string,
  pair: Pair
) {
  const { contract } = useContract(pairAddress);
  const { contract: token0Contract } = useContract(pair.token0.id, erc20);
  const { contract: token1Contract } = useContract(pair.token1.id, erc20);

  const { value, loading: loadingPair } = useAsync(async () => {
    if (!contract || !amount) {
      return;
    }

    const res = await contract.call('balanceOf', [pairAddress]);

    const decimals = await contract.call('decimals');

    const pairBalance = bigNumberToDecimal(res.toString(), decimals);

    const newPairBalance = pairBalance + +amount;

    const totalSupplyRes = await contract.call('totalSupply');

    const totalSupply = bigNumberToDecimal(totalSupplyRes.toString(), decimals);

    return {
      totalSupply,
      newPairBalance
    };
  }, [contract, pairAddress, amount]);

  const { value: balance0, loading: loadingBalance0 } = useAsync(async () => {
    if (!token0Contract) {
      return;
    }

    const res = await token0Contract.call('balanceOf', [pairAddress]);

    return bigNumberToDecimal(res.toString(), +pair.token0.decimals);
  }, [token0Contract, pairAddress, pair.token0]);

  const { value: balance1, loading: loadingBalance1 } = useAsync(async () => {
    if (!token1Contract) {
      return;
    }

    const res = await token1Contract.call('balanceOf', [pairAddress]);

    return bigNumberToDecimal(res.toString(), +pair.token1.decimals);
  }, [token1Contract, pairAddress, pair.token1]);

  const { value: output, loading } = useAsync(async () => {
    if (!value || !balance0 || !balance1) {
      return;
    }

    const { newPairBalance: liquidity, totalSupply } = value;

    const amount0 = (liquidity * balance0) / totalSupply;
    const amount1 = (liquidity * balance1) / totalSupply;

    return {
      amount0,
      amount1
    };
  }, [value, balance0, balance1]);

  return {
    output,
    loading: loading || loadingPair || loadingBalance0 || loadingBalance1
  };
}
